import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 relative pointer-events-auto",
  {
    variants: {
      variant: {
        default: "btn-variant-primary rounded-full",
        destructive: "btn-variant-destructive rounded-full",
        outline: "btn-variant-outline rounded-full",
        secondary: "btn-variant-secondary rounded-full",
        ghost: "btn-variant-ghost rounded-full",
        link: "text-primary underline-offset-4 hover:underline",
        brand: "btn-variant-brand rounded-full",
        success: "btn-variant-success rounded-full",
        warning: "bg-warning text-warning-foreground hover:bg-warning/90 rounded-full shadow-none",
        destructiveOutline: "border border-destructive text-destructive bg-background hover:bg-destructive/10 rounded-full shadow-none",
        'soft-primary': "btn-variant-soft-primary rounded-full",
        'soft-destructive': "btn-variant-soft-destructive rounded-full",
        'soft-success': "btn-variant-soft-success rounded-full",
      },
      size: {
        default: "h-10 min-h-[40px] px-6 text-sm",
        sm: "btn-size-sm",
        xs: "btn-size-xs",
        lg: "h-11 min-h-[44px] px-8 text-base rounded-full",
        icon: "btn-icon-only h-10 w-10 min-h-[40px] min-w-[40px]",
        "icon-sm": "btn-icon-only btn-size-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
